/**
 * "Booking link" button for the Appointment pages (Calendar / Services /
 * Bookings). Opens a popup with the direct URL to the public customer booking
 * page (/book/:companySlug) with Copy + Share.
 */
import { useState } from 'react';
import { Check, Copy, Link2, Loader2, Share2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { appointmentsService } from '@/services/appointments.service';

const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

export function BookingLinkButton() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [slug, setSlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await appointmentsService.bookingLink();
      setSlug(r.slug);
    } catch {
      setError("Couldn't load the booking link. Please try again.");
    }
    setLoading(false);
  };

  const onOpen = () => {
    setOpen(true);
    if (!slug && !loading) void load();
  };

  const url = slug ? `${window.location.origin}/book/${slug}` : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const share = async () => {
    if (canShare) {
      try { await navigator.share({ title: 'Book an appointment', url }); } catch { /* user cancelled */ }
    } else {
      void copy();
    }
  };

  return (
    <>
      <Button variant="secondary" size="sm" iconLeft={<Link2 size={15} />} onClick={onOpen}>
        Booking link
      </Button>

      <Modal open={open} onClose={() => setOpen(false)} title="Public booking link">
        <p className="text-[13px] text-text-muted mb-3">Share with customers to book & manage their own appointments.</p>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-[13px] text-text-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : error ? (
          <div className="rounded-md border border-danger/40 bg-danger-soft p-3 text-[13px] text-danger">{error}</div>
        ) : slug ? (
          <div className="rounded-xl border border-border p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-text-primary">Customer booking page</p>
                <p className="text-[11px] leading-snug text-text-muted">Customers pick a service, choose a slot, and book — no login needed.</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button variant="secondary" size="sm" onClick={copy} title="Copy link" aria-label="Copy link">
                  {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                </Button>
                {canShare && (
                  <Button variant="secondary" size="sm" onClick={share} title="Share link" aria-label="Share link">
                    <Share2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <p className="mt-2 truncate rounded-md bg-surface-alt px-2 py-1 font-mono text-[12px] text-text-muted" title={url} dir="ltr">
              {url}
            </p>
          </div>
        ) : null}
      </Modal>
    </>
  );
}
